import { FunctionComponent } from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';

const PREFIX = 'pages.application_season.components.season_step3_teams.counter';

type TeamsCounterProps = {
  selectedCount: number;
  allocatedCount: number;
  /** Quando true, exibe os contadores de alocação (HYBRID). */
  showAllocation: boolean;
};

/**
 * Barra de resumo sob os 2 painéis: "N times selecionados · X alocados · Y pendentes".
 * Sem alocação (LEAGUE/KNOCKOUT) exibe apenas a contagem total de selecionados.
 */
export const TeamsCounter: FunctionComponent<TeamsCounterProps> = ({
  selectedCount,
  allocatedCount,
  showAllocation,
}) => {
  const { t } = useTranslation();
  const pending = Math.max(selectedCount - allocatedCount, 0);

  if (selectedCount === 0) {
    return (
      <Flex
        align="center"
        gap={2}
        px={4}
        py={3}
        borderWidth="1px"
        borderStyle="dashed"
        borderColor="gray.200"
        borderRadius="md"
        color="gray.500"
      >
        <ChakraFeatherIcon name="users" boxSize="16px" />
        <Text fontSize="13px">{t(`${PREFIX}.empty`)}</Text>
      </Flex>
    );
  }

  return (
    <Flex
      align="center"
      gap={2}
      px={4}
      py={3}
      bg="orange.50"
      borderWidth="1px"
      borderColor="orange.100"
      borderRadius="md"
      color="orange.800"
    >
      <ChakraFeatherIcon name="users" boxSize="16px" />
      <Text fontSize="13px">
        <Text as="span" fontWeight="semibold">
          {selectedCount}
        </Text>{' '}
        {t(`${PREFIX}.selected`, { count: selectedCount })}
        {showAllocation ? (
          <>
            {' · '}
            <Text as="span" fontWeight="semibold">
              {allocatedCount}
            </Text>{' '}
            {t(`${PREFIX}.allocated`, { count: allocatedCount })}
            {' · '}
            <Text as="span" fontWeight="semibold">
              {pending}
            </Text>{' '}
            {t(`${PREFIX}.pending`, { count: pending })}
          </>
        ) : null}
      </Text>
    </Flex>
  );
};
